import { Bot, Check, ChevronDown, Inbox, Layers } from 'lucide-react';

import type {
  CandidateJobCountsApi,
  CandidateOriginApi,
} from '@/modules/candidates/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';

interface CandidateOriginFilterProps {
  /** The filtered origin; undefined shows every source. */
  value: CandidateOriginApi | undefined;
  /** The whole job's pipeline per origin, whatever the other filters. */
  counts: CandidateJobCountsApi['origin'] | undefined;
  onChange: (origin: CandidateOriginApi | undefined) => void;
}

/**
 * The "Source" filter. Candidate Origin is a separate dimension from status,
 * so it sits next to search rather than among the status filters.
 */
export function CandidateOriginFilter({
  value,
  counts,
  onChange,
}: CandidateOriginFilterProps) {
  const allSources = {
    origin: undefined,
    label: 'All sources',
    short: 'All',
    icon: Layers,
    count: counts && counts.inbound + counts.outbound,
  };
  const options = [
    allSources,
    {
      origin: 'Outbound' as const,
      label: 'Sourced by Finlay',
      short: 'Sourced by Finlay',
      icon: Bot,
      count: counts?.outbound,
    },
    {
      origin: 'Inbound' as const,
      label: 'Inbound',
      short: 'Inbound',
      icon: Inbox,
      count: counts?.inbound,
    },
  ];
  const selected =
    options.find((option) => option.origin === value) ?? allSources;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={`Source: ${selected.short}`}
          className="h-8 shrink-0 gap-1.5 rounded-lg px-2.5 text-[13px] font-normal"
          size="sm"
          variant="outline"
        >
          <span className="text-neutral-500">Source</span>
          <span className="font-medium">{selected.short}</span>
          <ChevronDown aria-hidden className="size-3.5 text-neutral-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {options.map((option) => {
          const isSelected = option === selected;
          const Icon = option.icon;
          return (
            <DropdownMenuItem
              aria-checked={isSelected}
              className="text-[13px] [&_svg]:!size-3.5"
              key={option.label}
              onSelect={() => onChange(option.origin)}
              role="menuitemradio"
            >
              <Icon aria-hidden className="text-neutral-500" />
              <span className="flex-1">{option.label}</span>
              {option.count !== undefined && (
                <span className="text-xs text-neutral-500 tabular-nums">
                  {option.count}
                </span>
              )}
              <Check
                aria-hidden
                className={isSelected ? undefined : 'invisible'}
              />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
